
import React, { useState, useEffect } from 'react';
import { checkEmailVerificationStatus, resendVerificationEmail } from '../services/storageService';
import { Sparkles, Mail, Castle, RefreshCw, Lightbulb } from 'lucide-react';

interface VerificationFormProps {
  email: string;
  onVerify: () => void;
  onCancel: () => void;
}

const VerificationForm: React.FC<VerificationFormProps> = ({ email, onVerify, onCancel }) => {
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    const interval = setInterval(async () => {
      const isVerified = await checkEmailVerificationStatus();
      if (isVerified) {
        clearInterval(interval);
        onVerify();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [onVerify]);

  const handleManualCheck = async () => {
    setChecking(true);
    setStatusMessage("Duke kontrolluar serverin...");
    try {
      const isVerified = await checkEmailVerificationStatus();
      if (isVerified) {
        onVerify();
      } else {
        setStatusMessage("Ende i paverifikuar. Klikoni linkun në email.");
        setTimeout(() => setStatusMessage(null), 5000);
      }
    } catch (err) {
      setStatusMessage("Gabim gjatë kontrollit.");
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setStatusMessage(null);
    try {
      await resendVerificationEmail();
      setStatusMessage("Sukses! Kontrolloni inbox-in tuaj tani");
    } catch (err: any) {
      setStatusMessage(err.message || "Pritni pak para se të provoni përsëri.");
    } finally {
      setResending(false);
      setTimeout(() => setStatusMessage(null), 8000);
    }
  };

  return (
    <div className="glass-card rounded-[2.5rem] p-8 md:p-12 max-w-md mx-auto border border-white/10 animate-in fade-in zoom-in duration-500 text-center relative overflow-hidden shadow-2xl">
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl"></div>
      
      <div className="mb-8 relative z-10">
        <div className="w-24 h-24 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce border-4 border-indigo-500/10 shadow-lg">
          <span className="text-5xl">📧</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-white font-fairytale mb-4 leading-tight">
          Hapi i fundit drejt magjisë!
        </h2>
        <p className="text-indigo-300/80 text-sm mb-6 leading-relaxed">
          Kemi dërguar linkun e verifikimit te:<br />
          <span className="text-indigo-100 font-bold text-base bg-white/5 px-3 py-1 rounded-lg inline-block mt-2 border border-white/10">{email}</span>
        </p>
        
        {statusMessage && (
          <div className={`p-4 rounded-2xl mb-6 text-xs font-bold animate-in fade-in slide-in-from-top-2 border shadow-lg ${statusMessage.includes('Sukses') ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'}`}>
            {statusMessage}
          </div>
        )}

        <div className="space-y-4">
          <button 
            onClick={handleManualCheck}
            disabled={checking}
            className="w-full light-pop-btn font-comic font-bold py-4 rounded-xl text-lg uppercase tracking-wide disabled:opacity-50 shadow-xl transition-all flex items-center justify-center gap-3"
          >
            {checking ? 'Duke kontrolluar...' : (
              <>
                E verifikova! Vazhdo <span className="text-2xl">🏰</span>
              </>
            )}
          </button>

          <button 
            onClick={handleResend}
            disabled={resending}
            className="w-full py-4 rounded-xl bg-white/5 border border-white/10 text-white font-bold text-sm hover:bg-white/10 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {resending ? 'Duke dërguar...' : (
              <>
                Nuk erdhi? Ridërgo linkun <span className="text-xl">🔄</span>
              </>
            )}
          </button>

          <div className="pt-6 space-y-3">
             <div className="p-4 bg-black/20 rounded-2xl text-left border border-white/5">
                <p className="text-[10px] text-indigo-300/40 font-black uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <span className="text-sm">💡</span> Këshilla:
                </p>
                <ul className="text-[11px] text-indigo-200/60 space-y-1.5 list-disc pl-4">
                  <li>Kontrolloni folderin <span className="text-white">"Spam"</span> ose <span className="text-white">"Junk"</span>.</li>
                  <li>Në Gmail, kontrolloni tab-in <span className="text-white">"Promotions"</span>.</li>
                  <li>Sigurohuni që emaili i mësipërm është i saktë.</li>
                </ul>
             </div>
             
             <button 
                type="button" 
                onClick={onCancel}
                className="text-indigo-300/40 hover:text-white text-[10px] font-black uppercase tracking-widest transition-colors"
              >
                ← Kthehu dhe provo tjetër email
              </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationForm;
